/**
 * aiConsentSignal — PROD-1225 (patient half). ONE place for the three decisions the
 * app makes about the server's AI consent gate:
 *
 *   1. SIGNAL. `apiFetch` and the raw SSE fetch of /ai/patient-summary-stream call
 *      `signalAiConsentRequired(status, data)` on every non-2xx. It dispatches the
 *      window event `orella:ai-consent-required` ONLY for a 403 whose JSON body says
 *      `code: 'ai_consent_required'` (the API's contract), and never for any other
 *      403 (2FA setup, case access, session lock). The caller still throws as before.
 *   2. LISTENER. `createReconsentListener` turns that event into ONE navigation to
 *      /ai-consent/renew with a return path. Several AI calls fail together when the
 *      gate is armed (a screen fires 2-4 at once), so the listener disarms after the
 *      first navigation and re-arms once the patient has LEFT the re-consent route, when that
 *      navigation was redirected elsewhere (next path change), and on a sign-in or sign-out. It never navigates while already on that route (no loop), nor out of
 *      /onboarding, whose own consent step comes first.
 *   3. LAUNCH CHECK. `createLaunchCheck` calls GET /ai-consent/status once per signed-in
 *      session. It asks for the re-consent screen only when the server says
 *      `enforced: true` and the patient has not consented. `enforced: false` (the switch
 *      AI_CONSENT_ENFORCE is off, the default) and any error both mean "do nothing":
 *      the server gate stays the authority and a failed status read never blocks the app.
 *   4. RECORD FAILURE TEXT. `consentRecordErrorText(err, fallback)` picks what the consent
 *      screen shows when POST /ai-consent fails. Only the server's 409
 *      `code: 'ai_consent_update_required'` (this build's disclosure version is older than the
 *      server's) shows the update sentence. The screen passes it as `updateText`, the localized
 *      `onboarding.aiConsent.updateRequired` (en, es); the server's own `error` sentence (the
 *      same words, English) is the fallback only when that key is missing. Every other failure (400 ai_consent_version_invalid, 500, network, timeout) shows
 *      `fallback`, the generic save error. The caller neither retries nor advances.
 *
 * Pure module, no React and no imports, so scripts/check-ai-consent-403.mjs drives the
 * real code in node. No PHI: the only payload is the disclosure version string.
 */

export const AI_CONSENT_EVENT = 'orella:ai-consent-required'
export const AI_CONSENT_CODE = 'ai_consent_required'
export const AI_CONSENT_UPDATE_CODE = 'ai_consent_update_required'
export const RECONSENT_PATH = '/ai-consent/renew'
export const RECONSENT_FALLBACK_RETURN = '/home'

/** True only for the server's consent refusal: HTTP 403 + `code: 'ai_consent_required'`. */
export function isAiConsentRequired(status, data) {
  return status === 403 && !!data && typeof data === 'object' && data.code === AI_CONSENT_CODE
}

/**
 * Dispatches `orella:ai-consent-required` (detail `{ version }`) when the response is the
 * consent refusal. Returns whether it did. Never throws: a signal must not replace the
 * error the caller is about to throw.
 */
export function signalAiConsentRequired(status, data, target) {
  if (!isAiConsentRequired(status, data)) return false
  const t = target || (typeof window !== 'undefined' ? window : null)
  try {
    if (t && typeof t.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
      t.dispatchEvent(new CustomEvent(AI_CONSENT_EVENT, { detail: { version: data.version ?? null } }))
    }
  } catch (_) { /* the caller's throw is what matters */ }
  return true
}

/** A return path is an in-app path, never the re-consent route itself and never off-site. */
export function safeReturnPath(p) {
  if (typeof p !== 'string' || !p.startsWith('/') || p.startsWith('//') || p.startsWith('/\\')) {
    return RECONSENT_FALLBACK_RETURN
  }
  const pathOnly = p.split(/[?#]/)[0]
  if (pathOnly === RECONSENT_PATH || pathOnly === '/onboarding' || pathOnly === '/welcome' || pathOnly === '/signin') {
    return RECONSENT_FALLBACK_RETURN
  }
  return p
}

/**
 * The event listener's state machine. `getPath()` returns the current pathname + search;
 * `navigate(to, opts)` is react-router's. `onPath(pathname)` is called on every route
 * change so the listener re-arms after the patient has been through the screen.
 */
export function createReconsentListener({ getPath, navigate }) {
  let armed = true
  let visited = false   // the patient has been on the re-consent route and not yet left it
  let pending = false   // we navigated and the route has not yet arrived at RECONSENT_PATH
  const onPath = (pathname) => {
    if (pathname === RECONSENT_PATH) { visited = true; pending = false; armed = false; return }
    // Our one navigation was redirected elsewhere (/signin, /onboarding): it never reached the
    // route, so the next path change re-arms. Otherwise re-arm after leaving the route.
    if (pending || visited) { pending = false; visited = false; armed = true }
  }
  // Sign-in or sign-out: a new session starts armed.
  const onAuthChange = () => { armed = true; visited = false; pending = false }
  const handle = () => {
    const here = String(getPath() || '')
    const pathOnly = here.split(/[?#]/)[0]
    if (pathOnly === RECONSENT_PATH) return false
    // Onboarding has its own consent step; leaving it mid-sign-up would strand the patient.
    if (pathOnly === '/onboarding' || pathOnly.startsWith('/onboarding/')) return false
    if (!armed) return false
    armed = false
    pending = true
    navigate(RECONSENT_PATH, { state: { returnTo: safeReturnPath(here) } })
    return true
  }
  return { handle, onPath, onAuthChange, isArmed: () => armed }
}

/** The launch decision: re-consent only when the server enforces AND has no current consent. */
export function launchNeedsReconsent(status) {
  return !!status && typeof status === 'object' && status.enforced === true && !status.consented
}

/**
 * Once-per-session launch check. `run(sessionKey)` resolves 'redirect' or 'ok' and calls
 * `fetchStatus` at most once per key (concurrent callers share the one request). Any error
 * resolves 'ok'. `markConsented()` is called after a successful re-consent so the cached
 * 'redirect' cannot send the patient back; `reset()` on sign-out.
 */
export function createLaunchCheck(fetchStatus) {
  let key = null
  let result = null
  let pending = null
  const run = (sessionKey) => {
    const k = sessionKey == null ? '' : String(sessionKey)
    if (k === key && result) return Promise.resolve(result)
    if (k === key && pending) return pending
    key = k
    result = null
    const p = Promise.resolve()
      .then(() => fetchStatus())
      .then((s) => (launchNeedsReconsent(s) ? 'redirect' : 'ok'), () => 'ok')
      .then((r) => { if (key === k && pending === p) { result = r; pending = null } return r })
    pending = p
    return p
  }
  const peek = (sessionKey) => {
    const k = sessionKey == null ? '' : String(sessionKey)
    return k === key ? result : null
  }
  const markConsented = () => { result = 'ok'; pending = null }
  const reset = () => { key = null; result = null; pending = null }
  return { run, peek, markConsented, reset }
}

/**
 * The text the consent screen shows for a failed POST /ai-consent (review N1). `err` is what
 * apiFetch throws: `{ status, ...body }`. The server's sentence only for the 409 update-required
 * code: `updateText` (the localized sentence) when it is a non-empty string, else the server's
 * `error` when that is a non-empty string; otherwise `fallback`.
 */
export function consentRecordErrorText(err, fallback, updateText) {
  if (err && typeof err === 'object' && err.status === 409 && err.code === AI_CONSENT_UPDATE_CODE) {
    if (typeof updateText === 'string' && updateText.trim()) return updateText
    if (typeof err.error === 'string' && err.error.trim()) return err.error
  }
  return fallback
}
